import React, { Component } from "react";
import { PageHeader, Spin, message, InputNumber, Checkbox } from "antd";
import axios from "axios";
import { connect } from "react-redux";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { ButtonItem, SelectEmpleadosItem, DatePickerItem } from "../../../../Components/Items";
import { datosTablaPrestacion } from "../sbc";
import moment from "moment";

// ── Helpers ────────────────────────────────────────────────────────────────
// La lectura de la tabla de prestaciones vive en Pages/Panel/Empleados/sbc.js, compartida con el
// botón F10 de Agregar/Editar Empleado y gemela de Utils/prestaciones.utils.js del backend. Aquí
// había una cuarta copia que medía la antigüedad desde `fechaAlta` en crudo, ignorando el
// reingreso, y dividiendo entre 365.25 — con eso el aniversario caía del lado equivocado.
const calcularNuevaSBC = (nuevoSalario, datosTabla) => {
  if (!nuevoSalario || !datosTabla?.factorIntegracion) return null;
  return parseFloat((nuevoSalario * datosTabla.factorIntegracion).toFixed(2));
};

const nuevaFila = (key) => ({
  key, empleadoId: null, empleado: null,
  datosTabla: null, nuevoSalario: null,
  nuevaSBC: null, motivo: '', fechaOverride: null, aplicar: true,
});

// ── Estilos de tabla ───────────────────────────────────────────────────────
const thBase = {
  padding: '6px 8px', fontSize: 11, fontWeight: 600,
  textAlign: 'center', border: '1px solid #b8a000',
  whiteSpace: 'nowrap', color: '#fff',
};
const TH  = { ...thBase, background: '#c9a227' };   // dorado — salario nuevo / aplicar
const THG = { ...thBase, background: '#5a7a8a', border: '1px solid #4a6a7a' }; // gris — info actual
const TD  = { border: '1px solid #e0e0e0', padding: '4px 6px', textAlign: 'center', fontSize: 12, verticalAlign: 'middle' };
const TDY = { ...TD, background: '#fffde7' };        // amarillo — celdas editables

class AumentoSueldo extends Component {
  state = {
    loading: false,
    fechaGlobal: moment(),
    filas: [nuevaFila(0)],
    keyCounter: 1,
  };

  atras = () => this.props.history.goBack();

  agregarFila = () =>
    this.setState(p => ({
      filas: [...p.filas, nuevaFila(p.keyCounter)],
      keyCounter: p.keyCounter + 1,
    }));

  eliminarFila = (key) =>
    this.setState(p => ({ filas: p.filas.filter(f => f.key !== key) }));

  actualizarFila = (key, cambios) =>
    this.setState(p => ({
      filas: p.filas.map(f => f.key === key ? { ...f, ...cambios } : f),
    }));

  onEmpleadoSelect = (key, empleadoId) => {
    if (!empleadoId) {
      this.actualizarFila(key, { empleadoId: null, empleado: null, datosTabla: null, nuevoSalario: null, nuevaSBC: null });
      return;
    }
    if (this.state.filas.some(f => f.key !== key && f.empleadoId === empleadoId)) {
      message.warning('Este empleado ya está en otra fila');
      return;
    }
    this.setState({ loading: true });
    axios.post(
      'empleados/datosEmpleadoParaAumento',
      { empleadoId, empresaId: this.props.empresaId },
      { headers: this.props.headersToken }
    ).then(res => {
      this.setState({ loading: false });
      if (res.data.success === 2) {
        const emp = res.data.empleado;
        // Se le pasa el empleado completo, no sólo la fecha de alta: `datosEmpleadoParaAumento`
        // devuelve el documento sin .select(), así que trae `fechaReingreso`, y un reingreso
        // reinicia la antigüedad y con ella el escalón de la tabla.
        // `antiguedadCumplidosSbc` es el criterio de antigüedad de la empresa para la fila de la
        // tabla: el mismo que usan F10 y la revisión del SBC.
        const datosTabla = datosTablaPrestacion({
          tipoPrestacion: emp.tipoPrestacion,
          empleado: emp,
          aniosCumplidos: res.data.antiguedadCumplidosSbc === true,
        });
        this.actualizarFila(key, { empleadoId, empleado: emp, datosTabla, nuevoSalario: null, nuevaSBC: null });
      }
    }).catch(() => this.setState({ loading: false }));
  };

  onNuevoSalarioChange = (key, valor) =>
    this.setState(p => ({
      filas: p.filas.map(f => {
        if (f.key !== key) return f;
        return { ...f, nuevoSalario: valor, nuevaSBC: calcularNuevaSBC(valor, f.datosTabla) };
      }),
    }));

  guardar = () => {
    const { filas, fechaGlobal } = this.state;
    const filasAplicar = filas.filter(f => f.aplicar && f.empleadoId && f.nuevoSalario > 0);
    if (!filasAplicar.length)
      return message.warning('No hay filas válidas con nuevo salario para aplicar');

    this.setState({ loading: true });
    axios.post(
      'empleados/registrarAumentoSalario',
      {
        empresaId: this.props.empresaId,
        aumentos: filasAplicar.map(f => ({
          empleadoId: f.empleadoId,
          salarioDiario: f.nuevoSalario,
          sbcParteFija: f.nuevaSBC,
          motivo: f.motivo,
          fecha: (f.fechaOverride || fechaGlobal).toISOString(),
        })),
      },
      { headers: this.props.headersToken }
    ).then(res => {
      this.setState({ loading: false });
      if (res.data.success === 2) {
        message.success(`Aumento aplicado a ${filasAplicar.length} empleado(s)`);
        this.atras();
      } else {
        message.error('Error al guardar');
      }
    }).catch(() => { this.setState({ loading: false }); message.error('Error al guardar'); });
  };

  render() {
    const { filas, fechaGlobal, loading } = this.state;
    const fmt = (n) => n != null ? `$${Number(n).toFixed(2)}` : '—';

    return (
      <>
        <PageHeader
          onBack={this.atras}
          title="Aumento de Sueldo"
          style={{ border: '1px solid rgb(235,237,240)', backgroundColor: 'white', marginBottom: 10 }}
          extra={
            <ButtonItem type="primary" onClick={this.guardar}>
              Aplicar Aumentos
            </ButtonItem>
          }
        />
        <div style={{ backgroundColor: 'white', padding: '16px 20px' }}>
          <Spin spinning={loading}>

            {/* Fecha global */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Fecha de aplicación:</span>
              <DatePickerItem
                value={fechaGlobal}
                format="DD/MM/YYYY"
                style={{ width: 160 }}
                onChange={(v) => this.setState({ fechaGlobal: v })}
              />
            </div>

            {/* Tabla */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1200 }}>
                <thead>
                  <tr>
                    <th rowSpan={2} style={{ ...THG, minWidth: 190 }}>Empleado</th>
                    <th colSpan={3} style={THG}>Salario actual</th>
                    <th colSpan={5} style={THG}>Prestaciones</th>
                    <th colSpan={2} style={TH}>Salario nuevo</th>
                    <th colSpan={3} style={TH}>Aplicar incremento de salario</th>
                    <th rowSpan={2} style={{ ...THG, width: 36 }} />
                  </tr>
                  <tr>
                    <th style={THG}>Sal. diario</th>
                    <th style={THG}>SBC (parte fija)</th>
                    <th style={THG}>F. Alta/Reingreso</th>
                    <th style={THG}>Antigüedad</th>
                    <th style={THG}>Días vac.</th>
                    <th style={THG}>% Prima vac.</th>
                    <th style={THG}>Días aguin.</th>
                    <th style={THG}>Factor integ.</th>
                    <th style={TH}>Salario diario</th>
                    <th style={TH}>SBC (parte fija)</th>
                    <th style={TH}>Fecha aplic.</th>
                    <th style={TH}>Motivo</th>
                    <th style={TH}>Aplicar</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((fila) => {
                    const emp = fila.empleado;
                    const dt  = fila.datosTabla;
                    return (
                      <tr key={fila.key}>

                        {/* Empleado */}
                        <td style={{ ...TD, padding: '2px 4px' }}>
                          <SelectEmpleadosItem
                            empresaId={this.props.empresaId}
                            headersToken={this.props.headersToken}
                            sinBorde width="100%"
                            placeholder="Buscar empleado..."
                            onChange={(v) => this.onEmpleadoSelect(fila.key, v)}
                          />
                        </td>

                        {/* Salario actual */}
                        <td style={TD}>{fmt(emp?.salarioDiario)}</td>
                        <td style={TD}>{fmt(emp?.sbcParteFija)}</td>
                        <td style={TD}>{emp?.fechaAlta ? moment(emp.fechaAlta).format('DD/MM/YYYY') : '—'}</td>

                        {/* Prestaciones */}
                        <td style={TD}>{dt ? dt.anios           : '—'}</td>
                        <td style={TD}>{dt ? dt.diasVacaciones  : '—'}</td>
                        <td style={TD}>{dt ? dt.primaVacacional : '—'}</td>
                        <td style={TD}>{dt ? dt.diasAguinaldo   : '—'}</td>
                        <td style={TD}>{dt ? dt.factorIntegracion : '—'}</td>

                        {/* Nuevo salario — editable */}
                        <td style={TDY}>
                          <InputNumber
                            value={fila.nuevoSalario}
                            min={0} precision={2} placeholder="0.00"
                            style={{ width: 90, border: 'none', background: 'transparent' }}
                            onChange={(v) => this.onNuevoSalarioChange(fila.key, v)}
                          />
                        </td>

                        {/* Nueva SBC — calculada */}
                        <td style={{ ...TD, color: fila.nuevaSBC ? '#2d7d46' : undefined, fontWeight: fila.nuevaSBC ? 600 : undefined }}>
                          {fmt(fila.nuevaSBC)}
                        </td>

                        {/* Fecha override */}
                        <td style={TDY}>
                          <DatePickerItem
                            value={fila.fechaOverride}
                            format="DD/MM/YYYY"
                            placeholder={fechaGlobal.format('DD/MM/YYYY')}
                            style={{ width: 120, border: 'none', background: 'transparent' }}
                            onChange={(v) => this.actualizarFila(fila.key, { fechaOverride: v })}
                          />
                        </td>

                        {/* Motivo */}
                        <td style={TDY}>
                          <input
                            value={fila.motivo}
                            placeholder="Motivo..."
                            style={{ width: '100%', border: 'none', background: 'transparent', outline: 'none', fontSize: 12 }}
                            onChange={(e) => this.actualizarFila(fila.key, { motivo: e.target.value })}
                          />
                        </td>

                        {/* Aplicar */}
                        <td style={TDY}>
                          <Checkbox
                            checked={fila.aplicar}
                            onChange={(e) => this.actualizarFila(fila.key, { aplicar: e.target.checked })}
                          />
                        </td>

                        {/* Eliminar fila */}
                        <td style={TD}>
                          <DeleteOutlined
                            style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: 14 }}
                            onClick={() => this.eliminarFila(fila.key)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <ButtonItem
              type="dashed" block icon={<PlusOutlined />}
              style={{ marginTop: 12 }}
              onClick={this.agregarFila}
            >
              Agregar empleado
            </ButtonItem>

          </Spin>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(AumentoSueldo);